/**
 * HunterIdentity (§20-25) — dá identidade VOID ao Hunter GLB (base CC0) SEM tocar no skeleton
 * (§26): anexa meshes low-poly (visor, cristais) a bones do rig. Para não depender dos eixos
 * locais (imprevisíveis) de cada bone, cada attachment é CONTRA-ROTACIONADO para os eixos de
 * MUNDO no bind pose (X=lado, Y=cima, Z=frente/+face) e posicionado por offset de mundo.
 * Como fica parented ao bone, ainda acompanha a animação. Mobile-friendly.
 */
import * as THREE from "three";
import type { RiggedVisual } from "./ModelFallbackFactory";

const CYAN = 0x8fe8ff;
const VIOLET = 0x9d7bff;
const _wq = new THREE.Quaternion();
const _inv = new THREE.Quaternion();
const _v = new THREE.Vector3();

function crystal(color: number, ei = 1.2): THREE.MeshStandardMaterial {
  return new THREE.MeshStandardMaterial({ color, emissive: color, emissiveIntensity: ei, roughness: 0.3, metalness: 0.2, flatShading: true });
}
function dark(): THREE.MeshStandardMaterial {
  return new THREE.MeshStandardMaterial({ color: 0x241f38, roughness: 0.9, metalness: 0.15, flatShading: true });
}

function findBone(root: THREE.Object3D, names: string[]): THREE.Object3D | null {
  const needles = names.map((n) => n.toLowerCase());
  let m: THREE.Object3D | null = null;
  root.traverse((o) => {
    if (m || !o.name) return;
    const n = o.name.toLowerCase();
    if (needles.some((x) => n.includes(x))) m = o;
  });
  return m;
}

/** Anexa `group` a `bone`, alinhado aos eixos de MUNDO e deslocado por `world` (X lado, Y cima,
 * Z frente). Considera o modelo com frente = +Z de mundo (rotationY já aplicado no root). */
function attachWorld(bone: THREE.Object3D, group: THREE.Group, world: [number, number, number]): void {
  bone.getWorldQuaternion(_wq);
  _inv.copy(_wq).invert();
  group.quaternion.copy(_inv);
  _v.set(world[0], world[1], world[2]).applyQuaternion(_inv); // offset de mundo → local do bone
  group.position.copy(_v);
  bone.add(group);
}

/** Anexa a identidade VOID ao Hunter. Chame após adicionar o rig à cena. */
export function attachHunterIdentity(v: RiggedVisual): void {
  const root = v.root;
  root.updateWorldMatrix(true, true); // garante world matrices no bind pose

  const head = findBone(root, ["Head", "mixamorigHead"]);
  const chest = findBone(root, ["spine_03", "spine_02", "Spine2", "Chest", "spine.003"]);
  const hips = findBone(root, ["spine_01", "pelvis", "Spine1", "spine.002"]);

  // ---- Head: capuz + visor de energia (frente = +Z de mundo) ----
  if (head) {
    const g = new THREE.Group();
    const hood = new THREE.Mesh(new THREE.ConeGeometry(0.16, 0.3, 7), dark());
    hood.position.set(0, 0.08, -0.03);
    g.add(hood);
    const visor = new THREE.Mesh(new THREE.BoxGeometry(0.22, 0.05, 0.05), crystal(CYAN, 1.8));
    visor.position.set(0, 0.02, 0.12);
    g.add(visor);
    attachWorld(head, g, [0, 0.12, 0]); // sobe até a cabeça
  }

  // ---- Chest: núcleo de cristal + pads de ombro (ciano/violeta) ----
  if (chest) {
    const g = new THREE.Group();
    const core = new THREE.Mesh(new THREE.OctahedronGeometry(0.08, 0), crystal(CYAN, 1.4));
    core.position.set(0, 0, 0.14);
    g.add(core);
    for (const sx of [-0.2, 0.2]) {
      const pad = new THREE.Mesh(new THREE.OctahedronGeometry(0.1, 0), crystal(VIOLET, 0.9));
      pad.position.set(sx, 0.06, 0);
      pad.scale.set(1, 0.5, 1);
      g.add(pad);
    }
    // farpas nas costas (silhueta vista de cima)
    for (const [bx, s] of [[0, 1], [-0.12, 0.7], [0.12, 0.7]] as [number, number][]) {
      const sp = new THREE.Mesh(new THREE.OctahedronGeometry(0.08, 0), crystal(CYAN, 1.0));
      sp.position.set(bx, 0.05, -0.14);
      sp.scale.setScalar(s);
      g.add(sp);
    }
    attachWorld(chest, g, [0, 0.02, 0]);
  }

  // ---- Quadril: cinto de energia discreto ----
  if (hips) {
    const g = new THREE.Group();
    const belt = new THREE.Mesh(new THREE.TorusGeometry(0.16, 0.03, 6, 12), crystal(VIOLET, 0.8));
    belt.rotation.x = Math.PI / 2;
    g.add(belt);
    attachWorld(hips, g, [0, 0.02, 0]);
  }
}
